import api from '@lib/axios';
import type { PaginatedResponse, Wallet, WalletTransaction } from '@witzy/shared';


export interface TransactionQueryParams {
  page?: number;
  limit?: number;
  category?: string;
  type?: 'credit' | 'debit';
  from?: string;
  to?: string;
}

export interface RazorpayOrder {
  order_id: string;
  /** Paise — total payable including GST. */
  amount: number;
  currency: string;
  key_id: string;
  /** Wallet credit (taxable value), ₹. */
  base_amount_inr: number;
  gst_rate_pct: number;
  gst_amount_inr: number;
  /** base + GST — what the customer actually pays, ₹. */
  total_amount_inr: number;
}

export type AutoPayMethod = 'card' | 'upi';

/** GET /wallets/auto-recharge — the rule, the saved auto-pay and any top-up in flight. */
export interface AutoRechargeView {
  /** False until the feature is switched on server-side; hide the entry point. */
  feature_available: boolean;
  enabled: boolean;
  threshold: number | null;
  amount: number | null;
  /** Why it switched itself off: `failures`, `mandate_cancelled`, `mandate_paused`, `amount_exceeds_mandate`. */
  paused_reason: string | null;
  gst_rate_pct: number;
  mandate: {
    id: string;
    method: AutoPayMethod;
    status: 'pending' | 'active' | 'paused' | 'cancelled' | 'rejected' | 'abandoned';
    display_label: string | null;
    max_amount_inr: number;
    confirmed_at: string | null;
  } | null;
  pending_attempt: {
    id: string;
    status: 'notifying' | 'scheduled' | 'charging';
    base_amount_inr: number;
    total_amount_inr: number;
    /** When the bank will debit; null until the advance notice is delivered. */
    payment_after: string | null;
    cancellable: boolean;
  } | null;
}

export interface AutoRechargeRule {
  enabled: boolean;
  threshold?: number;
  amount?: number;
}

/** Checkout options for approving auto-pay once. */
export interface AutoPaySetup {
  mandate_id: string;
  key_id: string;
  order_id: string;
  customer_id: string;
  method: AutoPayMethod;
  recurring: boolean | string;
  max_amount_inr: number;
}

export interface MonthlyBreakdown {
  month: string;
  marketing: { count: number; cost: number };
  utility: { count: number; cost: number };
  authentication: { count: number; cost: number };
  service: { count: number; cost: number };
  ai: { count: number; cost: number };
  total_cost: number;
  total_recharged: number;
}

export type WalletGateState = 'ok' | 'low' | 'suspend_warning' | 'blocked';

export interface WalletStatus {
  balance: number;
  /** Minimum recharge required to clear the block (negative balance + plan fees). */
  required_recharge_inr: number;
  state: WalletGateState;
  /** ISO date the service is suspended on (day before renewal), for the warning banner. */
  suspend_date: string | null;
  /** Unread INBOUND messages — surfaced on the blocker to motivate a recharge. */
  unread_count: number;
}

/** GST rate the server charges on top of a recharge (authoritative). */
export async function getGstRate(): Promise<{ gst_rate_pct: number }> {
  const { data } = await api.get<{ data: { gst_rate_pct: number } }>('/wallets/gst-rate');
  return data.data;
}

/** Wallet balance and settings. */
export async function getWallet(): Promise<Wallet> {
  const { data } = await api.get<{ data: Wallet }>('/wallets');
  return data.data;
}

/** Wallet gate status that drives the blocker overlay + warning banner. */
export async function getWalletStatus(projectId?: string | null): Promise<WalletStatus> {
  const { data } = await api.get<{ data: WalletStatus }>('/wallets/status', {
    params: projectId ? { project_id: projectId } : undefined,
  });
  return data.data;
}

/** Paginated transaction history with optional filters. */
export async function getTransactions(
  params: TransactionQueryParams = {},
): Promise<PaginatedResponse<WalletTransaction>> {
  const { data } = await api.get<{ data: PaginatedResponse<WalletTransaction> }>(
    '/wallets/transactions',
    { params },
  );
  return data.data;
}

/** Create a Razorpay payment order for wallet recharge. */
export async function createRechargeOrder(amount: number): Promise<RazorpayOrder> {
  const { data } = await api.post<{ data: RazorpayOrder }>('/wallets/recharge', { amount });
  return data.data;
}

/** Confirm a successful Razorpay payment. */
export async function confirmRecharge(
  paymentId: string,
  orderId: string,
  signature: string,
): Promise<void> {
  await api.post('/wallets/recharge/confirm', {
    razorpay_payment_id: paymentId,
    razorpay_order_id: orderId,
    razorpay_signature: signature,
  });
}

export async function getAutoRecharge(): Promise<AutoRechargeView> {
  const { data } = await api.get<{ data: AutoRechargeView }>('/wallets/auto-recharge');
  return data.data;
}

export async function updateAutoRecharge(rule: AutoRechargeRule): Promise<AutoRechargeView> {
  const { data } = await api.patch<{ data: AutoRechargeView }>('/wallets/auto-recharge', rule);
  return data.data;
}

export async function startAutoPay(method: AutoPayMethod): Promise<AutoPaySetup> {
  const { data } = await api.post<{ data: AutoPaySetup }>('/wallets/auto-recharge/mandate', {
    method,
  });
  return data.data;
}

/** Ask the server for the auto-pay's state after the payment window closes. */
export async function syncAutoPay(mandateId: string): Promise<AutoRechargeView> {
  const { data } = await api.post<{ data: AutoRechargeView }>(
    `/wallets/auto-recharge/mandate/${mandateId}/sync`,
  );
  return data.data;
}

export async function removeAutoPay(): Promise<AutoRechargeView> {
  const { data } = await api.delete<{ data: AutoRechargeView }>('/wallets/auto-recharge/mandate');
  return data.data;
}

export async function cancelAutoRechargeTopUp(attemptId: string): Promise<AutoRechargeView> {
  const { data } = await api.post<{ data: AutoRechargeView }>(
    `/wallets/auto-recharge/attempts/${attemptId}/cancel`,
  );
  return data.data;
}

/** Monthly cost breakdown (marketing, utility, auth, service, AI). */
export async function getMonthlyBreakdown(month: string): Promise<MonthlyBreakdown> {
  const { data } = await api.get<{ data: MonthlyBreakdown }>(
    `/wallets/breakdown/${month}`,
  );
  return data.data;
}
